import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
  persistReducer,
  persistStore,
  createMigrate,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  type PersistedState,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { useDispatch, useSelector, type TypedUseSelectorHook } from "react-redux";

import profileReducer from "./slices/profileSlice";
import settingsReducer from "./slices/settingsSlice";
import recentSearchesReducer from "./slices/recentSearchesSlice";
import bookingDraftReducer from "./slices/bookingDraftSlice";
import searchContextReducer from "./slices/searchContextSlice";
import selectedSuppliersReducer from "./slices/selectedSuppliersSlice";
import uiReducer from "./slices/uiSlice";
import autoConnectorReducer from "./slices/autoConnectorSlice";

const rootReducer = combineReducers({
  profile: profileReducer,
  settings: settingsReducer,
  recentSearches: recentSearchesReducer,
  bookingDraft: bookingDraftReducer,
  searchContext: searchContextReducer,
  selectedSuppliers: selectedSuppliersReducer,
  ui: uiReducer,
  autoConnector: autoConnectorReducer,
});

/**
 * Migrazioni redux-persist.
 * Bump `version` ogni volta che cambi la shape persistita o un enum value.
 */
const VALID_ACTIVE_ROLES = new Set(["OTA", "TRAVEL_AGENCY", "DMC", "BAD_BANK"]);

const migrations = {
  // v2: prima versione del nuovo enum ruolo.
  2: (state: PersistedState) => state,
  // v3: rollback temporaneo a {OTA, AGENCY} mentre il BE non era allineato.
  3: (state: PersistedState) => state,
  // v4: il BE booking-engine ora accetta i 4 valori del Controlplane.
  // Valori legacy (es. "AGENCY") vengono azzerati invece di essere
  // forzati a "TRAVEL_AGENCY": un default sbagliato fa fallire le API
  // con 403 prima del bootstrap. Meglio undefined → bootstrap ripopola.
  4: (state: PersistedState) => {
    if (!state) return state;
    const s = state as PersistedState & { profile?: { activeRole?: string } };
    const role = s.profile?.activeRole;
    if (role && !VALID_ACTIVE_ROLES.has(role)) {
      return {
        ...s,
        profile: { ...s.profile, activeRole: undefined },
      };
    }
    return s;
  },
  // v5: bookingDraft ora supporta multi-token (prebookSnapshots[]) e nuovi
  // campi per multi-room/agreements/expectedPrice/bookingGroup. Garantisce
  // l'esistenza degli array/flag su draft persistiti dalla v4.
  5: (state: PersistedState) => {
    if (!state) return state;
    const s = state as PersistedState & {
      bookingDraft?: Record<string, unknown>;
    };
    const old = s.bookingDraft ?? {};
    const oldSnap = (old as { prebookSnapshot?: unknown }).prebookSnapshot;
    return {
      ...s,
      bookingDraft: {
        ...old,
        prebookSnapshots: oldSnap ? [oldSnap] : [],
        agreementsAccepted: false,
        priceChangeAccepted: false,
      },
    };
  },
  // v6: `activeRole` non ha più un default hardcoded — viene popolato dal
  // bootstrap (`useOnboardingBootstrap` → GET /me/profile). Azzeriamo il
  // valore persistito: al prossimo mount del layout autenticato verrà
  // ripopolato con il valore vero. Sicuro anche per utenti già con
  // onboarding completato, perché il bootstrap rilegge le memberships.
  6: (state: PersistedState) => {
    if (!state) return state;
    const s = state as PersistedState & { profile?: { activeRole?: string } };
    if (!s.profile) return s;
    return {
      ...s,
      profile: { ...s.profile, activeRole: undefined },
    };
  },
  // v7: PrebookSnap ora include `amount_minor` (Stripe-ready integer). I draft
  // persistiti pre-v7 hanno solo `locked_price` (number EUR). Per non rompere
  // i draft in-flight, popoliamo amount_minor con fallback `round(locked_price
  // * 100)` (assumendo currency a 2 decimali — è il caso EUR/USD/GBP, e i
  // draft legacy in produzione sono quasi certamente EUR). Al prossimo
  // /prebook il BE sovrascrive con il valore corretto multi-valuta.
  7: (state: PersistedState) => {
    if (!state) return state;
    const s = state as PersistedState & {
      bookingDraft?: {
        prebookSnapshots?: Array<{
          locked_price?: number;
          amount_minor?: number;
        }>;
        prebookSnapshot?: { locked_price?: number; amount_minor?: number };
      };
    };
    const draft = s.bookingDraft;
    if (!draft) return s;
    if (Array.isArray(draft.prebookSnapshots)) {
      for (const snap of draft.prebookSnapshots) {
        if (snap.amount_minor === undefined && typeof snap.locked_price === "number") {
          snap.amount_minor = Math.round(snap.locked_price * 100);
        }
      }
    }
    if (draft.prebookSnapshot && draft.prebookSnapshot.amount_minor === undefined
        && typeof draft.prebookSnapshot.locked_price === "number") {
      draft.prebookSnapshot.amount_minor = Math.round(draft.prebookSnapshot.locked_price * 100);
    }
    return s;
  },
  // v8: bookingDraft ora ha `cancelIdempotencyKeys` e `amendIdempotencyKeys`
  // (mappe bookingId → uuid) per garantire idempotency su retry user-driven.
  // Inizializzate vuote per i draft pre-v8.
  8: (state: PersistedState) => {
    if (!state) return state;
    const s = state as PersistedState & {
      bookingDraft?: {
        cancelIdempotencyKeys?: Record<string, string>;
        amendIdempotencyKeys?: Record<string, string>;
      };
    };
    if (!s.bookingDraft) return s;
    if (!s.bookingDraft.cancelIdempotencyKeys) {
      s.bookingDraft.cancelIdempotencyKeys = {};
    }
    if (!s.bookingDraft.amendIdempotencyKeys) {
      s.bookingDraft.amendIdempotencyKeys = {};
    }
    return s;
  },
  // v9: `externalId` non deve mai contenere un org_id. Il commit "org switcher
  // multi-org in Pricing/Directory" lo riempiva con `o.org_id`, che il booking
  // engine valida come external_account_id → 400 su tutti i suoi endpoint.
  // Azzeriamo il valore persistito (avvelenato) così `X-External-Id` smette di
  // partire finché non esisterà un vero selettore di external_account. L'org-
  // context di pricing/directory viaggia su `X-Active-Role`, non qui.
  9: (state: PersistedState) => {
    if (!state) return state;
    const s = state as PersistedState & { profile?: { externalId?: string } };
    if (!s.profile) return s;
    return {
      ...s,
      profile: { ...s.profile, externalId: undefined },
    };
  },
};

const persistConfig = {
  key: "herika-suite",
  version: 9,
  storage,
  whitelist: ["profile", "settings", "recentSearches", "bookingDraft", "autoConnector"],
  migrate: createMigrate(migrations, { debug: false }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefault) =>
    getDefault({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: import.meta.env.DEV,
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
